import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, Users, CheckCircle2, XCircle, FileSpreadsheet, Lightbulb, Map, LogOut, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, responsesRes] = await Promise.all([
        axios.get(`${API}/survey/territory-stats`, { withCredentials: true }),
        axios.get(`${API}/survey/territory-responses`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setResponses(Array.isArray(responsesRes.data) ? responsesRes.data : []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/survey/export`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `enquete_batardeaux_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export Excel réussi');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F203C] mx-auto mb-4"></div>
          <p className="text-[#475569]">Chargement des données...</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const equipmentData = [
    { name: 'Avec batardeaux', value: stats?.with_batardeaux || 0, color: '#16A34A' },
    { name: 'Sans batardeaux', value: stats?.without_batardeaux || 0, color: '#DC2626' }
  ];

  // Analyze Q2 (Perception du risque)
  const riskPerception = responses.reduce((acc, r) => {
    if (r.q2) {
      acc[r.q2] = (acc[r.q2] || 0) + 1;
    }
    return acc;
  }, {});

  const riskData = Object.entries(riskPerception).map(([name, value]) => ({
    name: name.replace('Oui, ', '').replace('Non, ', ''),
    value
  }));

  // Analyze Q25 (Reasons for not installing)
  const reasonsNotInstalling = {};
  responses.forEach(r => {
    if (r.q25 && Array.isArray(r.q25)) {
      r.q25.forEach(reason => {
        reasonsNotInstalling[reason] = (reasonsNotInstalling[reason] || 0) + 1;
      });
    }
  });

  const reasonsData = Object.entries(reasonsNotInstalling)
    .map(([name, value]) => ({
      name: name.length > 30 ? name.substring(0, 30) + '...' : name,
      value
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  // Analyze Q4 (Water height)
  const waterHeight = responses.reduce((acc, r) => {
    if (r.q4) {
      acc[r.q4] = (acc[r.q4] || 0) + 1;
    }
    return acc;
  }, {});

  const waterHeightData = Object.entries(waterHeight).map(([name, value]) => ({
    name: name.length > 20 ? name.substring(0, 20) + '...' : name,
    value
  }));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F203C] text-white py-8 px-6 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 tracking-tight">Tableau de Bord Analytique</h1>
              <p className="text-lg opacity-90">Enquête Batardeaux - Mayane Résilience Center</p>
              {user && (
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-sm opacity-75">Connecté : {user.email}</span>
                  {user.territory_type && (
                    <span className="text-xs bg-[#D4AF37] text-[#0F203C] px-2 py-0.5 rounded-full font-semibold">
                      {user.territory_type === 'epci' ? `EPCI : ${user.territory_value}` : `Commune : ${user.territory_value}`}
                    </span>
                  )}
                  {!user.territory_type && user.role === 'admin' && (
                    <span className="text-xs bg-[#D4AF37] text-[#0F203C] px-2 py-0.5 rounded-full font-semibold">
                      Tous territoires
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 flex-wrap">
              {user?.role === 'admin' && (
                <Button 
                  data-testid="users-button"
                  onClick={() => navigate('/users')}
                  className="bg-white hover:bg-gray-100 text-[#0F203C] font-semibold"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Utilisateurs
                </Button>
              )}
              <Button 
                data-testid="map-button"
                onClick={() => navigate('/map')}
                className="bg-white hover:bg-gray-100 text-[#0F203C] font-semibold"
              >
                <Map className="w-4 h-4 mr-2" />
                Cartographie
              </Button>
              <Button 
                data-testid="insights-button"
                onClick={() => navigate('/insights')}
                className="bg-white hover:bg-gray-100 text-[#0F203C] font-semibold"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Insights IFOP
              </Button>
              <Button 
                data-testid="export-excel-button"
                onClick={handleExport}
                className="bg-[#D4AF37] hover:bg-[#b8962e] text-[#0F203C] font-semibold"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter vers Excel
              </Button>
              <Button 
                onClick={() => logout()}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#0F203C]"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-[#475569] uppercase tracking-wide flex items-center gap-2">
                <Users className="w-4 h-4" />
                Réponses Totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid="total-responses" className="text-4xl font-bold text-[#0F203C]">
                {stats?.total_responses || 0}
              </div>
              <p className="text-sm text-[#475569] mt-1">Participants à l'enquête</p>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-[#475569] uppercase tracking-wide flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Avec Batardeaux
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid="with-batardeaux" className="text-4xl font-bold text-[#16A34A]">
                {stats?.with_batardeaux || 0}
              </div>
              <p className="text-sm text-[#475569] mt-1">{stats?.percentage_equipped || 0}% des répondants</p>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-[#475569] uppercase tracking-wide flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Sans Batardeaux
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid="without-batardeaux" className="text-4xl font-bold text-[#DC2626]">
                {stats?.without_batardeaux || 0}
              </div>
              <p className="text-sm text-[#475569] mt-1">{100 - (stats?.percentage_equipped || 0)}% des répondants</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Charts */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Equipment Distribution */}
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#0F203C]">Répartition des Équipements</CardTitle>
              <CardDescription>Distribution des biens équipés vs non équipés</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={equipmentData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {equipmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Risk Perception */}
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#0F203C]">Perception du Risque</CardTitle>
              <CardDescription>Comment les répondants perçoivent le risque d'inondation</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={riskData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#475569' }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0F203C" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Water Height */}
          {waterHeightData.length > 0 && (
            <Card className="border-[#E2E8F0] shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#0F203C]">Hauteur d'Eau Maximale</CardTitle>
                <CardDescription>Distribution des hauteurs d'eau susceptibles d'impacter les biens</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={waterHeightData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 12 }} angle={-15} textAnchor="end" height={80} />
                    <YAxis tick={{ fill: '#475569' }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#D4AF37" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Reasons for not installing */}
          {reasonsData.length > 0 && (
            <Card className="border-[#E2E8F0] shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#0F203C]">Raisons de Non-Installation</CardTitle>
                <CardDescription>Principales raisons pour lesquelles les batardeaux n'ont pas été installés</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={reasonsData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fill: '#475569' }} />
                    <YAxis dataKey="name" type="category" tick={{ fill: '#475569', fontSize: 11 }} width={180} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#DC2626" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Data Table Preview */}
        <Card className="border-[#E2E8F0] shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-[#0F203C] flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Aperçu des Données
            </CardTitle>
            <CardDescription>Dernières {Math.min(responses.length, 10)} réponses collectées</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-3 px-4 font-semibold text-[#0F203C]">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#0F203C]">Équipé</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#0F203C]">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#0F203C]">Commune</th>
                  </tr>
                </thead>
                <tbody>
                  {responses.slice(-10).reverse().map((response, idx) => (
                    <tr key={idx} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors">
                      <td className="py-3 px-4 text-[#475569]">
                        {response.timestamp ? new Date(response.timestamp).toLocaleDateString('fr-FR') : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        {response.q7 === 'Oui' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Oui
                          </span>
                        ) : response.q7 === 'Non' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Non
                          </span>
                        ) : (
                          <span className="text-[#475569]">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-[#475569]">{response.rs1 || 'N/A'}</td>
                      <td className="py-3 px-4 text-[#475569]">{response.rs6 || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {responses.length === 0 && (
                <div className="text-center py-12 text-[#475569]">
                  Aucune réponse pour le moment
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
